import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './auth'

export const api = axios.create({
  baseURL: '/api/v1',
})

// Attach access token to every request
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers || ({} as any)
    ;(config.headers as any).Authorization = `Bearer ${token}`
  }
  return config
})

// Refresh-on-401 logic: if a request fails with 401, try to refresh once
let isRefreshing = false
let refreshQueue: Array<(t: string | null) => void> = []

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original: any = error.config
    if (!original || error.response?.status !== 401 || original._retried) {
      return Promise.reject(error)
    }
    original._retried = true

    if (isRefreshing) {
      // Queue this request until the in-flight refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push((newToken) => {
          if (newToken) {
            original.headers.Authorization = `Bearer ${newToken}`
            resolve(api(original))
          } else {
            reject(error)
          }
        })
      })
    }

    const refresh = getRefreshToken()
    if (!refresh) {
      clearTokens()
      return Promise.reject(error)
    }

    isRefreshing = true
    try {
      const r = await axios.post('/api/v1/auth/token/refresh/', { refresh })
      const newAccess: string = r.data.access
      const newRefresh: string | undefined = r.data.refresh
      setTokens(newAccess, newRefresh || refresh)
      refreshQueue.forEach((cb) => cb(newAccess))
      refreshQueue = []
      original.headers.Authorization = `Bearer ${newAccess}`
      return api(original)
    } catch (e) {
      refreshQueue.forEach((cb) => cb(null))
      refreshQueue = []
      clearTokens()
      return Promise.reject(e)
    } finally {
      isRefreshing = false
    }
  }
)
